/**
 * sessionGuard: signed-in reads must never run with the anon key.
 *
 * WHY (anon-valets review 2026-09-30, PE seat map §3 class B): AuthContext's
 * valets select('*') ran once as anon (2026-09-29 19:05 ET) because the
 * session token was missing or stale when it fired. The client believed it had
 * a user, but PostgREST saw the anon JWT. The fix is to wait for a verified
 * session instead of widening anon grants.
 *
 * A session is verified when it has an access token, has not expired, and (if a
 * user id is given) belongs to that user. No imports, so node --test can load it.
 */

export function isVerifiedSession(session, userId = null, now = Date.now()) {
  if (!session || typeof session.access_token !== 'string' || session.access_token.length === 0) return false;
  if (session.expires_at && session.expires_at * 1000 <= now) return false;
  if (userId && session.user?.id !== userId) return false;
  return true;
}

/** Resolves the verified session or null. Never rejects; a hung getSession counts as unverified. */
export async function getVerifiedSession(authClient, userId = null, { timeoutMs = 4000, now } = {}) {
  try {
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('getSession timed out')), timeoutMs);
    });
    const result = await Promise.race([authClient.getSession(), timeout]).finally(() => clearTimeout(timer));
    const session = result?.data?.session ?? null;
    if (result?.error) return null;
    return isVerifiedSession(session, userId, now ?? Date.now()) ? session : null;
  } catch {
    return null;
  }
}

export class NoVerifiedSessionError extends Error {
  constructor(message = 'No verified session yet. Please wait a moment and try again.') {
    super(message);
    this.name = 'NoVerifiedSessionError';
    this.code = 'NO_VERIFIED_SESSION';
  }
}

/** Like getVerifiedSession, but throws NoVerifiedSessionError instead of returning null. */
export async function requireVerifiedSession(authClient, userId = null, opts) {
  const session = await getVerifiedSession(authClient, userId, opts);
  if (!session) throw new NoVerifiedSessionError();
  return session;
}

/**
 * Explicit columns for AuthContext's valetProfile (was select('*')). Every field
 * read from useAuth().valetProfile across odv-truest, plus what
 * syncOAuthDataToProfile reads. Add a column here only when a consumer needs it.
 */
export const VALET_PROFILE_COLUMNS = [
  'id', 'auth_user_id', 'user_id', 'first_name', 'last_name', 'name', 'display_name',
  'avatar_url', 'level', 'status', 'discord_user_id', 'discord_username',
].join(', ');
